import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, forkJoin, Subject } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { BaseService } from '../../services/base.service';

export interface DroneSurveyBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Which tiles actually exist, as run-length encoded row ranges per column.
 *
 * Shape: { zoom: { x: [[yStart, yEnd], ...] } }
 *
 * A survey footprint is an irregular polygon, so roughly 57% of the coordinates
 * inside its bounding box have no tile behind them. Requesting those wastes a
 * round trip each, and behind CloudFront they return the app's index.html
 * rather than nothing, which Google Maps would try to draw as an image.
 *
 * The whole thing is about 18 KB, under 4 KB gzipped, for 47 322 tiles.
 */
export interface TileManifest {
  version: number;
  tiles: { [zoom: string]: { [x: string]: [number, number][] } };
}

/** One published drone tile pyramid, as returned by analysis-management. */
export interface DroneSurvey {
  /** UUID. The service's internal integer key is never exposed. */
  id: string;
  tenantId: number;
  name: string;
  /** Full tile URL with {z}/{x}/{y} placeholders. */
  tileUrlTemplate: string;
  minZoom: number;
  maxZoom: number;
  bounds: DroneSurveyBounds;
  /** ISO date, or null when the capture date is unknown. */
  capturedAt: string | null;
  /**
   * Populated client-side from the manifest that sits beside the tiles. Absent
   * if it could not be loaded, in which case the resolver falls back to the
   * bounding box derived from `bounds`.
   */
  manifest?: TileManifest;
}

/** Where the manifest sits relative to the tile template. */
export function manifestUrlFor(survey: DroneSurvey): string {
  return survey.tileUrlTemplate.replace('{z}/{x}/{y}.png', 'manifest.json');
}

// Root-provided: consumed by both the maps module and the older mapping
// screen, which lives in AppModule and cannot see MapsModule's providers.
@Injectable({ providedIn: 'root' })
export class DroneSurveyService extends BaseService {
  private readonly API_DRONE_SURVEY_URL = this.API_BASE_URL + 'analysis-management/droneSurveys';
  private readonly API_TILE_ACCESS_URL = this.API_BASE_URL + 'analysis-management/droneTileAccess';

  readonly surveys = signal<DroneSurvey[]>([]);
  readonly loading = signal(false);
  /** True once a load has completed, successfully or not. */
  readonly loaded = signal(false);
  /**
   * Emits when tile access could not be renewed. Screens listen so they can
   * detach the overlay: the fade control then disappears, which is at least
   * visible, rather than imagery quietly failing to draw.
   */
  readonly accessLost$ = new Subject<void>();

  /** Fraction of the cookie's life after which it is renewed. */
  private static readonly RENEW_AT = 0.8;

  private renewTimer: ReturnType<typeof setTimeout> | null = null;
  private renewTenantId: number | null = null;

  constructor(private http: HttpClient) {
    super();
  }

  /**
   * Stop renewing. Called when a map screen is destroyed, so a tab left open
   * does not keep refreshing access to imagery nobody is looking at.
   */
  stopRenewingTileAccess(): void {
    if (this.renewTimer) {
      clearTimeout(this.renewTimer);
      this.renewTimer = null;
    }
    this.renewTenantId = null;
  }

  /**
   * Renew the tile cookie before it expires.
   *
   * Proactive rather than reactive because the failure is invisible from here:
   * the tile path shares a distribution with the app, whose 403 handler serves
   * index.html with status 200, so a refused tile arrives as HTML rather than
   * an error, and ImageMapType offers no hook to notice.
   */
  private scheduleTileAccessRenewal(tenantId: number, expiresAt: string | null): void {
    this.stopRenewingTileAccess();
    this.renewTenantId = tenantId;

    const expiry = expiresAt ? Date.parse(expiresAt) : NaN;
    const lifetime = Number.isNaN(expiry) ? 3600_000 : expiry - Date.now();
    // Never sooner than a minute, so a clock skew or a short-lived cookie
    // cannot turn this into a request loop.
    const delay = Math.max(60_000, lifetime * DroneSurveyService.RENEW_AT);

    this.renewTimer = setTimeout(() => {
      if (this.renewTenantId !== tenantId) return;
      this.grantTileAccess(tenantId).subscribe(res => {
        if (res) return;
        console.warn('[DroneSurveyService] tile access could not be renewed');
        this.stopRenewingTileAccess();
        this.accessLost$.next();
      });
    }, delay);
  }

  /**
   * Load the tenant's surveys.
   *
   * Failures are swallowed to an empty list rather than left to the global
   * interceptor. This is imagery enrichment, not a feature the user asked for,
   * so a tenant with no surveys configured, or an endpoint not yet deployed to
   * a given environment, must not raise a snackbar on every map load.
   */
  loadSurveys(tenantId: number | null): Observable<DroneSurvey[]> {
    if (!tenantId) {
      this.surveys.set([]);
      this.loaded.set(true);
      return of([]);
    }
    this.loading.set(true);
    return this.http
      .get<DroneSurvey[]>(`${this.API_DRONE_SURVEY_URL}?tenantId=${tenantId}`)
      .pipe(
        catchError(err => {
          console.warn('[DroneSurveyService] could not load drone surveys', err?.status ?? err);
          return of([] as DroneSurvey[]);
        }),
        // The tiles are private. Nothing can be fetched until CloudFront has
        // issued a cookie, so this must complete before the overlay attaches.
        switchMap(surveys => {
          const list = Array.isArray(surveys) ? surveys : [];
          if (!list.length) return of(list);
          return this.grantTileAccess(tenantId).pipe(switchMap(() => this.withManifests(list)));
        }),
        tap(surveys => {
          this.surveys.set(surveys);
          this.loading.set(false);
          this.loaded.set(true);
        })
      );
  }

  /**
   * Ask the API for CloudFront signed cookies covering the tile path.
   *
   * withCredentials is required twice over: so the browser sends its session,
   * and so it will store the Set-Cookie that comes back. The cookie is
   * HttpOnly, so nothing here reads it; the browser attaches it to tile
   * requests by itself.
   */
  private grantTileAccess(tenantId: number): Observable<{ expiresAt: string } | null> {
    return this.http
      .get<{ expiresAt: string }>(`${this.API_TILE_ACCESS_URL}?tenantId=${tenantId}`, { withCredentials: true })
      .pipe(
        tap(res => this.scheduleTileAccessRenewal(tenantId, res?.expiresAt ?? null)),
        catchError(err => {
          console.warn('[DroneSurveyService] could not obtain tile access', err?.status ?? err);
          return of(null);
        })
      );
  }

  /**
   * Fetch each survey's tile manifest, so the resolver only requests tiles that
   * exist. Same origin as the app and the cookie is already set, so this is a
   * plain GET.
   *
   * A failure is not fatal: the survey keeps rendering from its bounding box,
   * which is correct but wasteful.
   */
  private withManifests(surveys: DroneSurvey[]): Observable<DroneSurvey[]> {
    return forkJoin(
      surveys.map(survey =>
        this.http.get<TileManifest>(manifestUrlFor(survey), { withCredentials: true }).pipe(
          map(manifest =>
            manifest && manifest.tiles ? { ...survey, manifest } : survey
          ),
          catchError(err => {
            console.warn('[DroneSurveyService] no tile manifest for', survey.name, err?.status ?? err);
            return of(survey);
          })
        )
      )
    );
  }
}
